import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import UserAgentBadge from "components/UserAgentBadge.jsx";
import formatters from "helpers/formatters.jsx";

export default observer(function AdminDisneyTokens(props) {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedToken, setSelectedToken] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({costPrice: "", salePrice: ""});
  const [csvFile, setCsvFile] = useState(null);
  const [csvContent, setCsvContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTokenIds, setSelectedTokenIds] = useState([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkNewStatus, setBulkNewStatus] = useState("");
  const [showNuplinModal, setShowNuplinModal] = useState(null); // 'initiate-purchase' | 'deactivate' | 'change-tier' | 'resubscribe'
  const [nuplinToken, setNuplinToken] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [changeTierForm, setChangeTierForm] = useState({product: "", policy: "IMMEDIATE"});
  const [initiatePurchaseForm, setInitiatePurchaseForm] = useState({sku: "", months: 1});
  const [webhookEventsToken, setWebhookEventsToken] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loadingWebhookEvents, setLoadingWebhookEvents] = useState(false);
  const [tokenClicksToken, setTokenClicksToken] = useState(null);
  const [tokenClicks, setTokenClicks] = useState([]);
  const [loadingTokenClicks, setLoadingTokenClicks] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [showBulkDeactivateModal, setShowBulkDeactivateModal] = useState(false);
  const [bulkDeactivateInput, setBulkDeactivateInput] = useState("");
  const [bulkDeactivateReason, setBulkDeactivateReason] = useState("");
  const [bulkDeactivatePreview, setBulkDeactivatePreview] = useState(null);
  const [bulkDeactivateResults, setBulkDeactivateResults] = useState(null);
  const [showBulkChangeTierModal, setShowBulkChangeTierModal] = useState(false);
  const [bulkChangeTierInput, setBulkChangeTierInput] = useState("");
  const [bulkChangeTierForm, setBulkChangeTierForm] = useState({toProduct: "", policy: "IMMEDIATE"});
  const [bulkChangeTierPreview, setBulkChangeTierPreview] = useState(null);
  const [bulkChangeTierResults, setBulkChangeTierResults] = useState(null);

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  const handleEditToken = (token) => {
    setSelectedToken(token);
    setEditForm({
      costPrice: token.costPrice || "",
      salePrice: token.salePrice || ""
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const {name, value} = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitEditToken = async () => {
    try {
      if (!editForm.costPrice && !editForm.salePrice) {
        alert("Por favor ingresa al menos un precio");
        return;
      }

      setLoading(true);
      await appDataStore.post("/disney-tokens/update", {
        id: selectedToken.id,
        costPrice: editForm.costPrice ? parseFloat(editForm.costPrice) : undefined,
        salePrice: editForm.salePrice ? parseFloat(editForm.salePrice) : undefined
      });

      alert("Token actualizado exitosamente");
      setShowEditModal(false);
      setSelectedToken(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al actualizar token:", err);
      alert("Error al actualizar token: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (token, newStatus) => {
    if (!confirm(`¿Cambiar estado del token a ${newStatus}?`)) {
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post("/disney-tokens/change-status", {
        id: token.id,
        status: newStatus
      });

      alert("Estado actualizado exitosamente");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al cambiar estado:", err);
      alert("Error al cambiar estado: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const submitCsvImport = async () => {
    try {
      if (!csvContent) {
        alert("Por favor selecciona un archivo CSV");
        return;
      }

      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/import-csv", {
        csvContent
      });

      alert(
        `Importación completada:\n` +
        `Total: ${response.total}\n` +
        `Exitosos: ${response.success}\n` +
        `Errores: ${response.errors}`
      );

      setShowUploadModal(false);
      setCsvFile(null);
      setCsvContent("");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al importar CSV:", err);
      alert("Error al importar CSV: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectToken = (tokenId, isSelected) => {
    setSelectedTokenIds(prev => {
      if (isSelected) {
        return [...prev, tokenId];
      } else {
        return prev.filter(id => id !== tokenId);
      }
    });
  };

  const handleSelectAllTokens = (table) => {
    const allTokenIds = table.getRowModel().rows.map(row => row.original.id);
    if (selectedTokenIds.length === allTokenIds.length) {
      setSelectedTokenIds([]);
    } else {
      setSelectedTokenIds(allTokenIds);
    }
  };

  const submitBulkStatusChange = async () => {
    try {
      if (!bulkNewStatus) {
        alert("Por favor selecciona un estado");
        return;
      }

      if (!selectedTokenIds.length) {
        alert("Por favor selecciona al menos un token");
        return;
      }

      if (!confirm(`¿Cambiar estado a "${bulkNewStatus}" para ${selectedTokenIds.length} tokens?`)) {
        return;
      }

      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-status", {
        ids: selectedTokenIds,
        status: bulkNewStatus
      });

      alert(`Estados actualizados: ${response.updated} tokens`);
      setShowBulkStatusModal(false);
      setBulkNewStatus("");
      setSelectedTokenIds([]);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al cambiar estados:", err);
      alert("Error al cambiar estados: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openNuplinModal = (type, token = null) => {
    setNuplinToken(token);
    setDeactivateReason("");
    setChangeTierForm({product: "", policy: "IMMEDIATE"});
    setInitiatePurchaseForm({sku: "", months: 1});
    setShowNuplinModal(type);
  };

  const closeNuplinModal = () => {
    setShowNuplinModal(null);
    setNuplinToken(null);
  };

  const submitInitiatePurchase = async () => {
    if (!initiatePurchaseForm.sku) return alert("Selecciona un SKU");
    try {
      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/initiate-purchase", initiatePurchaseForm);
      alert(`Compra iniciada exitosamente\nSubject ID: ${response.token?.subjectId || '-'}`);
      closeNuplinModal();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al iniciar compra: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitDeactivate = async () => {
    if (!deactivateReason) return alert("Ingresa el motivo de desactivación");
    if (!confirm(`¿Desactivar el entitlement ${nuplinToken.entitlementId}?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/disney-tokens/deactivate", {id: nuplinToken.id, reason: deactivateReason});
      alert("Desactivación programada exitosamente");
      closeNuplinModal();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al desactivar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitChangeTier = async () => {
    if (!changeTierForm.product) return alert("Selecciona el producto destino");
    if (changeTierForm.product === nuplinToken.product) return alert("El producto destino debe ser diferente al actual");
    if (!confirm(`¿Cambiar tier de "${nuplinToken.product}" a "${changeTierForm.product}"?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/disney-tokens/change-tier", {id: nuplinToken.id, ...changeTierForm});
      alert("Cambio de tier completado exitosamente");
      closeNuplinModal();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al cambiar tier: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitResubscribe = async () => {
    if (!confirm(`¿Resuscribir el entitlement ${nuplinToken.entitlementId}?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/disney-tokens/resubscribe", {id: nuplinToken.id});
      alert("Resuscripción completada exitosamente");
      closeNuplinModal();
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al resuscribir: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openBulkDeactivateModal = () => {
    setBulkDeactivateInput("");
    setBulkDeactivateReason("");
    setBulkDeactivatePreview(null);
    setBulkDeactivateResults(null);
    setShowBulkDeactivateModal(true);
  };

  const parsedEntitlementIds = bulkDeactivateInput
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  const fetchBulkDeactivatePreview = async () => {
    if (parsedEntitlementIds.length === 0) return alert("Ingresa al menos un Entitlement ID");
    try {
      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-deactivate-preview", {
        entitlementIds: parsedEntitlementIds
      });
      setBulkDeactivatePreview(response.rows);
    } catch (err) {
      alert("Error al generar preview: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkDeactivate = async () => {
    if (!bulkDeactivateReason) return alert("Ingresa el motivo de cancelación");
    if (!confirm(`¿Confirmar cancelación de ${parsedEntitlementIds.length} entitlements en Nuplin?`)) return;
    try {
      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-deactivate", {
        entitlementIds: parsedEntitlementIds,
        reason: bulkDeactivateReason
      });
      setBulkDeactivateResults(response);
      setBulkDeactivatePreview(null);
    } catch (err) {
      alert("Error en cancelación masiva: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openBulkChangeTierModal = () => {
    setBulkChangeTierInput("");
    setBulkChangeTierForm({toProduct: "", policy: "IMMEDIATE"});
    setBulkChangeTierPreview(null);
    setBulkChangeTierResults(null);
    setShowBulkChangeTierModal(true);
  };

  const parsedChangeTierIds = bulkChangeTierInput
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  const fetchBulkChangeTierPreview = async () => {
    if (parsedChangeTierIds.length === 0) return alert("Ingresa al menos un Entitlement ID");
    try {
      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-change-tier-preview", {
        entitlementIds: parsedChangeTierIds
      });
      setBulkChangeTierPreview(response.rows);
    } catch (err) {
      alert("Error al generar preview: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkChangeTier = async () => {
    if (!bulkChangeTierForm.toProduct) return alert("Selecciona el producto destino");
    if (!confirm(`¿Cambiar tier a "${bulkChangeTierForm.toProduct}" para ${parsedChangeTierIds.length} entitlements?`)) return;
    try {
      setLoading(true);
      const response = await appDataStore.post("/disney-tokens/bulk-change-tier", {
        entitlementIds: parsedChangeTierIds,
        toProduct: bulkChangeTierForm.toProduct,
        policy: bulkChangeTierForm.policy
      });
      setBulkChangeTierResults(response);
      setBulkChangeTierPreview(null);
    } catch (err) {
      alert("Error en cambio de tier masivo: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openTokenClicks = async (token) => {
    setTokenClicksToken(token);
    setTokenClicks([]);
    try {
      setLoadingTokenClicks(true);
      const res = await appDataStore.post('/app/datasource/TokenClick', {
        records: null,
        currentPage: 1,
        order: [['createdAt', 'DESC']],
        filter: [{key: 'disneyTokenId', value: token.id, operator: 'eq'}],
        attributes: ['id', 'ip', 'userAgent', 'createdAt'],
      });
      setTokenClicks(res.data || []);
    } catch (err) {
      alert("Error al cargar clicks: " + err.message);
    } finally {
      setLoadingTokenClicks(false);
    }
  };

  const openWebhookEvents = async (token) => {
    setWebhookEventsToken(token);
    setWebhookEvents([]);
    setExpandedPayload(null);
    try {
      setLoadingWebhookEvents(true);
      const res = await appDataStore.post("/disney-tokens/webhook-events", {disneyTokenId: token.id});
      setWebhookEvents(res.events || []);
    } catch (err) {
      alert("Error al cargar eventos: " + err.message);
    } finally {
      setLoadingWebhookEvents(false);
    }
  };

  const DISNEY_PRODUCTS = [
    {value: "bundle.premium", label: "Bundle Premium"},
    {value: "bundle.swads", label: "Bundle SWADS"},
    {value: "bundle.standard", label: "Bundle Standard"},
    {value: "standalone.premium", label: "Standalone Premium"},
    {value: "standalone.swads", label: "Standalone SWADS"},
    {value: "standalone.standard", label: "Standalone Standard"},
  ];

  const StatusBadge = ({status}) => {
    const statusColors = {
      available: "bg-green-100 text-green-800",
      used: "bg-blue-100 text-blue-800",
      expired: "bg-red-100 text-red-800",
      error: "bg-gray-100 text-gray-800"
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  const columns = useMemo(() => ([
    {
      id: 'select',
      header: ({table}) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={() => handleSelectAllTokens(table)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
        />
      ),
      cell: ({row}) => (
        <input
          type="checkbox"
          checked={selectedTokenIds.includes(row.original.id)}
          onChange={(e) => handleSelectToken(row.original.id, e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
        />
      ),
      enableColumnFilter: false,
      size: 40
    },
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({cell}) => {
        return cell.getValue();
      }
    },
    {
      accessorKey: 'subjectId',
      header: 'Subject ID',
    },
    {
      accessorKey: 'entitlementId',
      header: 'Entitlement ID',
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'validity',
      header: 'Vigencia',
      cell: ({cell}) => {
        const v = cell.getValue()
        return v ? `${v} ${v === 1 ? 'mes' : 'meses'}` : '—'
      }
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => <StatusBadge status={cell.getValue()}/>
    },
    {
      accessorKey: 'costPrice',
      header: 'Costo',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.costPrice
    },
    {
      accessorKey: 'salePrice',
      header: 'Precio Venta',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.salePrice
    },
    {
      accessorKey: 'nuplinState',
      header: 'Estado Nuplin',
      opFilter: 'startsWith',
      cell: ({cell}) => {
        const val = cell.getValue()
        if (!val) return <span className="text-gray-400 text-xs">—</span>
        const colors = {
          COMPLETED: 'bg-green-100 text-green-800',
          CANCELED_SCHEDULED: 'bg-red-100 text-red-800',
          DOWNGRADE_SCHEDULE: 'bg-yellow-100 text-yellow-800',
          UPGRADE_SCHEDULE: 'bg-blue-100 text-blue-800',
        }
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[val] || 'bg-gray-100 text-gray-700'}`}>{val}</span>
      }
    },
    {
      accessorKey: 'nuplinToken',
      header: 'Nuplin Token',
      enableColumnFilter: true,
      opFilter: 'substring',
      cell: ({cell}) => {
        const val = cell.getValue()
        if (!val) return <span className="text-gray-400 text-xs">—</span>
        return (
          <span className="font-mono text-xs text-gray-600 break-all line-clamp-1" title={val}>
            {val.length > 20 ? `…${val.slice(-20)}` : val}
          </span>
        )
      }
    },
    {
      accessorKey: 'activationDate',
      header: 'Fecha Activación',
      cell: ({cell}) => cell.getValue() ? moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss') : <span className="text-gray-400 text-xs">—</span>,
      exportFormat: (row) => row.activationDate ? moment(row.activationDate).format('YYYY-MM-DD HH:mm:ss') : ''
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha Creación',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => {
        const token = row.original;
        const isOpen = openActionMenu?.id === token.id;
        return (
          <div className="relative">
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setOpenActionMenu(isOpen ? null : {id: token.id, top: rect.bottom + window.scrollY, right: window.innerWidth - rect.right});
              }}
              disabled={loading}
              className="px-3 py-1 bg-blue-800 hover:bg-blue-900 text-white rounded text-xs font-medium flex items-center gap-1"
            >
              Acciones <span className="text-blue-200">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div
                style={{position: 'fixed', top: openActionMenu.top, right: openActionMenu.right, zIndex: 9999}}
                className="w-44 bg-white border border-gray-200 rounded shadow-lg"
                onMouseLeave={() => setOpenActionMenu(null)}
              >
                <button
                  onClick={() => { handleEditToken(token); setOpenActionMenu(null); }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Editar precios
                </button>
                {token.status === 'available' && (
                  <button
                    onClick={() => { handleChangeStatus(token, 'used'); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-green-700 hover:bg-green-50"
                  >
                    Marcar Usado
                  </button>
                )}
                {token.status !== 'expired' && (
                  <button
                    onClick={() => { handleChangeStatus(token, 'expired'); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50"
                  >
                    Expirar
                  </button>
                )}
                {token.status === 'used' && (
                  <button
                    onClick={() => { openNuplinModal('deactivate', token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-orange-600 hover:bg-orange-50"
                  >
                    Desactivar
                  </button>
                )}
                {token.status === 'used' && (
                  <button
                    onClick={() => { openNuplinModal('change-tier', token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-purple-600 hover:bg-purple-50"
                  >
                    Cambiar Tier
                  </button>
                )}
                {(token.status === 'used' || token.status === 'expired') && (
                  <button
                    onClick={() => { openNuplinModal('resubscribe', token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-teal-600 hover:bg-teal-50"
                  >
                    Resuscribir
                  </button>
                )}
                <div className="border-t border-gray-100"/>
                <button
                  onClick={() => { openWebhookEvents(token); setOpenActionMenu(null); }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-500 hover:bg-gray-50"
                >
                  Ver Eventos
                </button>
                <button
                  onClick={() => { openTokenClicks(token); setOpenActionMenu(null); }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-500 hover:bg-gray-50"
                >
                  Ver Clicks
                </button>
              </div>
            )}
          </div>
        );
      }
    }
  ]), [selectedTokenIds, loading, openActionMenu]);

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order: [['id', 'DESC']],
        search,
        filter,
        attributes: ['id', 'subjectId', 'entitlementId', 'product', 'status', 'providerId', 'costPrice', 'salePrice', 'nuplinState', 'nuplinToken', 'activationDate', 'validity', 'createdAt'],
        searchAttributes: ['subjectId', 'entitlementId', 'product', 'providerId', 'nuplinToken']
      };

      const response = await appDataStore.post("/disney-tokens/list", requestData);

      return {
        data: response.data || [],
        totalRows: response.total || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar tokens:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  if (!hasMasterScope) {
    return (
      <>
        <div className="p-2 max-w-7xl mx-auto text-black">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Tokens Disney</h2>
          <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
                <p className="text-red-700">
                  No tienes permisos para acceder a la gestión de tokens. Se requiere el scope <strong>Master</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-2  mx-auto text-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800"></h2>
          <div className="flex gap-2">
            <button
              onClick={() => openNuplinModal('initiate-purchase')}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-medium"
              disabled={loading}
            >
              🛒 Generar token
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-medium"
              disabled={loading}
            >
              📤 Importar CSV
            </button>
            <button
              onClick={openBulkChangeTierModal}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium"
              disabled={loading}
            >
              Cambio de Tier Masivo
            </button>
            <button
              onClick={openBulkDeactivateModal}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              disabled={loading}
            >
              Cancelación Masiva
            </button>
            {selectedTokenIds.length > 0 && (
              <button
                onClick={() => setShowBulkStatusModal(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium"
                disabled={loading}
              >
                ⚙️ Cambiar Estado ({selectedTokenIds.length})
              </button>
            )}
          </div>
        </div>

        <DataTable
          key={refreshKey}
          columns={columns}
          dataFetch={dataFetch}
          enablePagination
          enableSearch
          enableColumnFilter
          enableExport
          exportFileName="disney-tokens"
        />
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Editar Token</h3>

            <div className="space-y-4 mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Subject ID</p>
                <p className="text-gray-800 font-medium">{selectedToken.subjectId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Entitlement ID</p>
                <p className="text-gray-800 font-medium">{selectedToken.entitlementId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Producto</p>
                <p className="text-gray-800 font-medium">{selectedToken.product}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Costo
                </label>
                <input
                  type="number"
                  name="costPrice"
                  value={editForm.costPrice}
                  onChange={handleEditFormChange}
                  placeholder="0.00"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio de Venta
                </label>
                <input
                  type="number"
                  name="salePrice"
                  value={editForm.salePrice}
                  onChange={handleEditFormChange}
                  placeholder="0.00"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitEditToken}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-medium"
                disabled={loading}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Importar Tokens desde CSV</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Archivo CSV
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              {csvFile && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Archivo seleccionado: {csvFile.name}
                </p>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded">
              <p className="font-medium mb-2">Formato esperado:</p>
              <p>El CSV debe contener las columnas:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>Subject ID</li>
                <li>Entitlement ID</li>
                <li>Product</li>
                <li>Nuplin Token</li>
              </ul>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setCsvFile(null);
                  setCsvContent("");
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitCsvImport}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-medium"
                disabled={loading || !csvContent}
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initiate Purchase Modal */}
      {showNuplinModal === 'initiate-purchase' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Generar nuevo Token</h3>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto (SKU)</label>
                <select
                  value={initiatePurchaseForm.sku}
                  onChange={e => setInitiatePurchaseForm(prev => ({...prev, sku: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Seleccionar...</option>
                  {DISNEY_PRODUCTS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meses de suscripción</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={initiatePurchaseForm.months}
                  onChange={e => setInitiatePurchaseForm(prev => ({...prev, months: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium" disabled={loading}>Cancelar</button>
              <button onClick={submitInitiatePurchase} className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-medium" disabled={loading}>Iniciar Compra</button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Modal */}
      {showNuplinModal === 'deactivate' && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Desactivar Entitlement</h3>
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-gray-50 rounded text-sm">
                <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
                <p><span className="text-gray-500">Entitlement ID:</span> <span className="font-medium">{nuplinToken.entitlementId}</span></p>
                <p><span className="text-gray-500">Producto:</span> <span className="font-medium">{nuplinToken.product}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de desactivación</label>
                <input
                  type="text"
                  value={deactivateReason}
                  onChange={e => setDeactivateReason(e.target.value)}
                  placeholder="Ej: User Request"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium" disabled={loading}>Cancelar</button>
              <button onClick={submitDeactivate} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium" disabled={loading}>Desactivar</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Tier Modal */}
      {showNuplinModal === 'change-tier' && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Tier</h3>
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-gray-50 rounded text-sm">
                <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
                <p><span className="text-gray-500">Producto actual:</span> <span className="font-medium">{nuplinToken.product}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto destino</label>
                <select
                  value={changeTierForm.product}
                  onChange={e => setChangeTierForm(prev => ({...prev, product: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Seleccionar...</option>
                  {DISNEY_PRODUCTS.filter(p => p.value !== nuplinToken.product).map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Política</label>
                <select
                  value={changeTierForm.policy}
                  onChange={e => setChangeTierForm(prev => ({...prev, policy: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="IMMEDIATE">Inmediato</option>
                  <option value="">Diferido (fin de ciclo)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium" disabled={loading}>Cancelar</button>
              <button onClick={submitChangeTier} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded font-medium" disabled={loading}>Cambiar Tier</button>
            </div>
          </div>
        </div>
      )}

      {/* Resubscribe Modal */}
      {showNuplinModal === 'resubscribe' && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Resuscribir Entitlement</h3>
            <div className="p-3 bg-gray-50 rounded text-sm mb-4">
              <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
              <p><span className="text-gray-500">Entitlement ID:</span> <span className="font-medium">{nuplinToken.entitlementId}</span></p>
              <p><span className="text-gray-500">Producto:</span> <span className="font-medium">{nuplinToken.product}</span></p>
              <p><span className="text-gray-500">Meses:</span> <span className="font-medium">{nuplinToken.validity}</span></p>
            </div>
            <p className="text-sm text-gray-600 mb-4">Se creará una nueva instancia de suscripción para este entitlement.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium" disabled={loading}>Cancelar</button>
              <button onClick={submitResubscribe} className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded font-medium" disabled={loading}>Resuscribir</button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Events Modal */}
      {webhookEventsToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[90vw] max-h-[98vh] flex flex-col shadow-lg">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Eventos Nuplin</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Subject: <span className="font-medium text-gray-700">{webhookEventsToken.subjectId}</span>
                  {" · "}
                  Entitlement: <span className="font-medium text-gray-700">{webhookEventsToken.entitlementId}</span>
                </p>
              </div>
              <button
                onClick={() => { setWebhookEventsToken(null); setExpandedPayload(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {loadingWebhookEvents ? (
                <div className="text-center py-8 text-gray-500">Cargando eventos...</div>
              ) : webhookEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No hay eventos registrados para este token.</div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Fecha</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Código</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Tipo</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Proveedor</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Mensaje</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Payload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhookEvents.map((event) => (
                      <>
                        <tr key={event.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{moment(event.receivedAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                          <td className="px-3 py-2 text-gray-800 font-mono text-xs">{event.code}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{event.eventType || '-'}</td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{event.provider || '-'}</td>
                          <td className="px-3 py-2 text-gray-700 max-w-xs truncate">{event.message || '-'}</td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => setExpandedPayload(expandedPayload === event.id ? null : event.id)}
                              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs"
                            >
                              {expandedPayload === event.id ? 'Cerrar' : 'Ver'}
                            </button>
                          </td>
                        </tr>
                        {expandedPayload === event.id && (
                          <tr key={`${event.id}-payload`} className="bg-gray-50">
                            <td colSpan={6} className="px-3 py-2">
                              <pre className="text-xs text-gray-700 bg-gray-100 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                                {JSON.stringify(event.rawPayload, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Token Clicks Modal */}
      {tokenClicksToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[90vw] max-h-[98vh] flex flex-col shadow-lg">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Clicks del Token</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Subject: <span className="font-medium text-gray-700">{tokenClicksToken.subjectId}</span>
                  {" · "}
                  Entitlement: <span className="font-medium text-gray-700">{tokenClicksToken.entitlementId}</span>
                </p>
              </div>
              <button
                onClick={() => setTokenClicksToken(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
              >×</button>
            </div>
            <div className="overflow-y-auto flex-1">
              {loadingTokenClicks ? (
                <div className="text-center py-8 text-gray-500">Cargando clicks...</div>
              ) : tokenClicks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No hay clicks registrados para este token.</div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Fecha</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">IP</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Navegador / SO / Dispositivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokenClicks.map((click, i) => (
                      <tr key={click.id} className={`border-b border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{moment(click.createdAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                        <td className="px-3 py-2 text-gray-800 font-mono text-xs">{click.ip || '-'}</td>
                        <td className="px-3 py-2"><UserAgentBadge ua={click.userAgent} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Change Tier Modal */}
      {showBulkChangeTierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[96vw] max-h-[96vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Cambio de Tier Masivo</h3>
              <button
                onClick={() => setShowBulkChangeTierModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            {/* Paso 1: Ingreso */}
            {!bulkChangeTierPreview && !bulkChangeTierResults && (
              <>
                <div className="space-y-4 mb-4 flex-1 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entitlement IDs <span className="text-gray-400 font-normal">(uno por línea, o separados por coma)</span>
                    </label>
                    <textarea
                      value={bulkChangeTierInput}
                      onChange={e => setBulkChangeTierInput(e.target.value)}
                      placeholder={"novaENT_1001\nnovaENT_1002\nnovaENT_1003"}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">{parsedChangeTierIds.length} entitlements ingresados</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowBulkChangeTierModal(false)}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={fetchBulkChangeTierPreview}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                    disabled={loading || parsedChangeTierIds.length === 0}
                  >
                    {loading ? "Consultando..." : "Ver Reporte"}
                  </button>
                </div>
              </>
            )}

            {/* Paso 2: Preview */}
            {bulkChangeTierPreview && !bulkChangeTierResults && (
              <>
                <div className="flex-1 overflow-y-auto mb-4">
                  <div className="flex gap-3 mb-3 text-sm">
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      Total: <strong>{bulkChangeTierPreview.length}</strong>
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      Encontrados: <strong>{bulkChangeTierPreview.filter(r => r.found).length}</strong>
                    </span>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                      No encontrados: <strong>{bulkChangeTierPreview.filter(r => !r.found).length}</strong>
                    </span>
                  </div>

                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Entitlement ID</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Producto actual</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Estado</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Cliente</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Distribuidor</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Última actualización</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b border-gray-300">Días desde update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkChangeTierPreview.map((row, i) => (
                        <tr key={i} className={`border-b border-gray-200 ${!row.found ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">{row.entitlementId}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{row.found ? row.product : '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            {row.found
                              ? <span className={`px-2 py-0.5 rounded font-medium ${
                                  row.status === 'used' ? 'bg-blue-100 text-blue-800' :
                                  row.status === 'available' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{row.status}</span>
                              : <span className="text-red-500 font-medium">No encontrado</span>
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.found
                              ? row.customerName
                                ? <div>
                                    <p className="font-medium">{row.customerName}</p>
                                    {row.email && <p className="text-gray-400">{row.email}</p>}
                                    {row.phoneNumber && <p className="text-gray-400">{row.phoneNumber}</p>}
                                  </div>
                                : <span className="text-gray-400">—</span>
                              : '—'
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.found
                              ? row.distributorName
                                ? <div>
                                    <p className="font-medium">{row.distributorName}</p>
                                    {row.distributorEmail && <p className="text-gray-400">{row.distributorEmail}</p>}
                                  </div>
                                : <span className="text-gray-400">—</span>
                              : '—'
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                            {row.found ? moment(row.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.found
                              ? <span className={`text-sm font-bold ${
                                  row.daysSinceUpdate >= 30 ? 'text-red-600' :
                                  row.daysSinceUpdate >= 7 ? 'text-yellow-600' :
                                  'text-gray-700'
                                }`}>{row.daysSinceUpdate}d</span>
                              : '—'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Producto destino</label>
                      <select
                        value={bulkChangeTierForm.toProduct}
                        onChange={e => setBulkChangeTierForm(prev => ({...prev, toProduct: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        {DISNEY_PRODUCTS.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Política</label>
                      <select
                        value={bulkChangeTierForm.policy}
                        onChange={e => setBulkChangeTierForm(prev => ({...prev, policy: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="IMMEDIATE">Inmediato</option>
                        <option value="">Diferido (fin de ciclo)</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setBulkChangeTierPreview(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                      disabled={loading}
                    >
                      Volver
                    </button>
                    <button
                      onClick={submitBulkChangeTier}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium"
                      disabled={loading || !bulkChangeTierForm.toProduct}
                    >
                      {loading ? "Procesando..." : `Cambiar tier a ${bulkChangeTierForm.toProduct || '...'} (${bulkChangeTierPreview.filter(r => r.found).length} entitlements)`}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Paso 3: Resultados */}
            {bulkChangeTierResults && (
              <>
                <div className="flex-1 overflow-y-auto">
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-800">{bulkChangeTierResults.total}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{bulkChangeTierResults.success?.length || 0}</p>
                      <p className="text-xs text-green-600">Exitosos</p>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{bulkChangeTierResults.errors?.length || 0}</p>
                      <p className="text-xs text-red-600">Errores</p>
                    </div>
                  </div>
                  {bulkChangeTierResults.errors?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-red-700 mb-2">Errores:</p>
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {bulkChangeTierResults.errors.map((e, i) => (
                          <div key={i} className="text-xs bg-red-50 border border-red-200 rounded px-3 py-2">
                            <span className="font-mono text-gray-700">{e.entitlementId}</span>
                            <span className="text-red-600 ml-2">— {e.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  <button
                    onClick={() => { setShowBulkChangeTierModal(false); setRefreshKey(prev => prev + 1); }}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => { setBulkChangeTierResults(null); setBulkChangeTierInput(""); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium"
                  >
                    Nuevo Cambio
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Deactivate Modal */}
      {showBulkDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[96vw] max-h-[96vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Cancelación Masiva de Entitlements</h3>
              <button
                onClick={() => setShowBulkDeactivateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            {/* Paso 1: Ingreso de IDs */}
            {!bulkDeactivatePreview && !bulkDeactivateResults && (
              <>
                <div className="space-y-4 mb-4 flex-1 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entitlement IDs <span className="text-gray-400 font-normal">(uno por línea, o separados por coma)</span>
                    </label>
                    <textarea
                      value={bulkDeactivateInput}
                      onChange={e => setBulkDeactivateInput(e.target.value)}
                      placeholder={"novaENT_1001\nnovaENT_1002\nnovaENT_1003"}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">{parsedEntitlementIds.length} entitlements ingresados</p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowBulkDeactivateModal(false)}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={fetchBulkDeactivatePreview}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                    disabled={loading || parsedEntitlementIds.length === 0}
                  >
                    {loading ? "Consultando..." : "Ver Reporte"}
                  </button>
                </div>
              </>
            )}

            {/* Paso 2: Preview / Reporte */}
            {bulkDeactivatePreview && !bulkDeactivateResults && (
              <>
                <div className="flex-1 overflow-y-auto mb-4">
                  <div className="flex gap-3 mb-3 text-sm">
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      Total: <strong>{bulkDeactivatePreview.length}</strong>
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      Encontrados: <strong>{bulkDeactivatePreview.filter(r => r.found).length}</strong>
                    </span>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                      No encontrados: <strong>{bulkDeactivatePreview.filter(r => !r.found).length}</strong>
                    </span>
                  </div>

                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Entitlement ID</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Producto</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Estado</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Cliente</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Distribuidor</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Última actualización</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-700 border-b border-gray-300">Días desde update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkDeactivatePreview.map((row, i) => (
                        <tr key={i} className={`border-b border-gray-200 ${!row.found ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">{row.entitlementId}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{row.found ? row.product : '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            {row.found
                              ? <span className={`px-2 py-0.5 rounded font-medium ${
                                  row.status === 'used' ? 'bg-blue-100 text-blue-800' :
                                  row.status === 'available' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{row.status}</span>
                              : <span className="text-red-500 font-medium">No encontrado</span>
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.found
                              ? row.customerName
                                ? <div>
                                    <p className="font-medium">{row.customerName}</p>
                                    {row.email && <p className="text-gray-400">{row.email}</p>}
                                    {row.phoneNumber && <p className="text-gray-400">{row.phoneNumber}</p>}
                                  </div>
                                : <span className="text-gray-400">—</span>
                              : '—'
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {row.found
                              ? row.distributorName
                                ? <div>
                                    <p className="font-medium">{row.distributorName}</p>
                                    {row.distributorEmail && <p className="text-gray-400">{row.distributorEmail}</p>}
                                  </div>
                                : <span className="text-gray-400">—</span>
                              : '—'
                            }
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                            {row.found ? moment(row.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.found
                              ? <span className={`text-sm font-bold ${
                                  row.daysSinceUpdate >= 30 ? 'text-red-600' :
                                  row.daysSinceUpdate >= 7 ? 'text-yellow-600' :
                                  'text-gray-700'
                                }`}>{row.daysSinceUpdate}d</span>
                              : '—'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t pt-4">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de cancelación</label>
                    <input
                      type="text"
                      value={bulkDeactivateReason}
                      onChange={e => setBulkDeactivateReason(e.target.value)}
                      placeholder="Ej: User Request"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setBulkDeactivatePreview(null)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                      disabled={loading}
                    >
                      Volver
                    </button>
                    <button
                      onClick={submitBulkDeactivate}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                      disabled={loading || !bulkDeactivateReason}
                    >
                      {loading ? "Procesando..." : `Cancelar ${bulkDeactivatePreview.filter(r => r.found).length} entitlements`}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Paso 3: Resultados */}
            {bulkDeactivateResults && (
              <>
                <div className="flex-1 overflow-y-auto">
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-800">{bulkDeactivateResults.total}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{bulkDeactivateResults.success?.length || 0}</p>
                      <p className="text-xs text-green-600">Exitosos</p>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{bulkDeactivateResults.errors?.length || 0}</p>
                      <p className="text-xs text-red-600">Errores</p>
                    </div>
                  </div>
                  {bulkDeactivateResults.errors?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-red-700 mb-2">Errores:</p>
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {bulkDeactivateResults.errors.map((e, i) => (
                          <div key={i} className="text-xs bg-red-50 border border-red-200 rounded px-3 py-2">
                            <span className="font-mono text-gray-700">{e.entitlementId}</span>
                            <span className="text-red-600 ml-2">— {e.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  <button
                    onClick={() => { setShowBulkDeactivateModal(false); setRefreshKey(prev => prev + 1); }}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => { setBulkDeactivateResults(null); setBulkDeactivateInput(""); }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                  >
                    Nueva Cancelación
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Status Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Estado - {selectedTokenIds.length} Tokens</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Estado
              </label>
              <select
                value={bulkNewStatus}
                onChange={(e) => setBulkNewStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">Seleccionar...</option>
                <option value="available">Disponible</option>
                <option value="used">Usado</option>
                <option value="expired">Expirado</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowBulkStatusModal(false);
                  setBulkNewStatus("");
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={submitBulkStatusChange}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium"
                disabled={loading || !bulkNewStatus}
              >
                Cambiar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});